import { useEffect, useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { PhoneOff, Mic, MicOff, Video, VideoOff, Volume2, VolumeX, SwitchCamera, Phone, ZoomIn, ZoomOut } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useWebRTC, CallStatus } from "@/hooks/useWebRTC";

interface CallViewProps {
  chatId: string;
  currentUserId: string;
  isVideo: boolean;
  displayName: string;
  avatarUrl: string | null;
  isIncoming?: boolean;
  onClose: () => void;
}

const formatDuration = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
};

const statusLabels: Record<CallStatus, string> = {
  idle: "",
  calling: "Chiamata in corso...",
  ringing: "In arrivo...",
  connected: "Connesso",
  ended: "Chiamata terminata",
};

export const CallView = ({
  chatId, currentUserId, isVideo: initialIsVideo, displayName, avatarUrl, isIncoming, onClose,
}: CallViewProps) => {
  const {
    status, isMuted, isCameraOff, isVideoEnabled, isSpeaker, callDuration,
    localVideoRef, remoteVideoRef, remoteAudioRef,
    startCall, answerCall, endCall, toggleMute, toggleCamera, toggleSpeaker,
    flipCamera, facingMode,
  } = useWebRTC({ chatId, currentUserId, isVideo: initialIsVideo });

  const [zoomLevel, setZoomLevel] = useState(1);
  const [hasRemoteVideo, setHasRemoteVideo] = useState(false);

  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev + 0.5, 4));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev - 0.5, 1));
  }, []);

  // Monitor remote video track
  useEffect(() => {
    const videoEl = remoteVideoRef.current;
    if (!videoEl) return;
    const checkStream = () => {
      const stream = videoEl.srcObject as MediaStream | null;
      if (stream) {
        const hasVid = stream.getVideoTracks().some(t => t.enabled && t.readyState === "live");
        setHasRemoteVideo(hasVid);
      }
    };
    const interval = setInterval(checkStream, 500);
    return () => clearInterval(interval);
  }, [remoteVideoRef]);

  useEffect(() => {
    if (isIncoming) {
      answerCall(initialIsVideo);
    } else {
      startCall(initialIsVideo);
    }
  }, []);

  useEffect(() => {
    if (status === "ended") {
      const t = setTimeout(onClose, 1500);
      return () => clearTimeout(t);
    }
  }, [status, onClose]);

  const showVideo = isVideoEnabled || hasRemoteVideo;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col bg-black/95 backdrop-blur-2xl"
    >
      {/* Hidden audio element for speakerphone control */}
      <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" />

      {/* Remote video (fullscreen) */}
      {showVideo && (
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-200"
          style={{ transform: `scale(${zoomLevel})` }}
        />
      )}

      {/* Local video (PiP) */}
      {showVideo && !isCameraOff && (
        <div className="absolute top-16 right-4 w-28 h-40 rounded-2xl overflow-hidden shadow-2xl ring-2 ring-white/20 z-10">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover mirror"
            style={{ transform: "scaleX(-1)" }}
          />
        </div>
      )}

      {/* Audio-only header */}
      {!showVideo && (
        <div className="flex-1 flex flex-col items-center justify-center gap-6 relative z-10">
          <div className="relative">
            {status === "calling" && (
              <motion.div
                className="absolute inset-0 rounded-full bg-primary/20"
                animate={{ scale: [1, 1.6, 1], opacity: [0.5, 0, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            )}
            <Avatar className="w-28 h-28 ring-4 ring-white/20">
              <AvatarImage src={avatarUrl || undefined} />
              <AvatarFallback className="bg-primary/20 text-white text-3xl font-semibold">
                {displayName.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </div>
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white">{displayName}</h2>
            <p className="text-white/60 text-sm mt-1">
              {status === "connected" ? formatDuration(callDuration) : statusLabels[status]}
            </p>
          </div>
        </div>
      )}

      {/* Video overlay info */}
      {showVideo && (
        <div className="absolute top-4 left-4 z-10 flex items-center gap-3">
          <div className="bg-black/40 backdrop-blur-md rounded-full px-4 py-2 flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${status === "connected" ? "bg-green-400" : "bg-yellow-400 animate-pulse"}`} />
            <span className="text-white text-sm font-medium">
              {status === "connected" ? formatDuration(callDuration) : statusLabels[status]}
            </span>
          </div>
        </div>
      )}

      {/* Zoom controls */}
      {showVideo && (
        <div className="absolute bottom-36 right-4 z-10 flex flex-col gap-2">
          <button
            onClick={handleZoomIn}
            disabled={zoomLevel >= 4}
            className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-md text-white flex items-center justify-center disabled:opacity-30 transition-all active:scale-90"
          >
            <ZoomIn className="w-5 h-5" />
          </button>
          {zoomLevel > 1 && (
            <span className="text-white text-xs font-medium text-center bg-black/40 rounded-full px-2 py-0.5 backdrop-blur-md">
              {zoomLevel.toFixed(1)}x
            </span>
          )}
          <button
            onClick={handleZoomOut}
            disabled={zoomLevel <= 1}
            className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-md text-white flex items-center justify-center disabled:opacity-30 transition-all active:scale-90"
          >
            <ZoomOut className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Controls */}
      <div className="relative z-10 pb-12 pt-6">
        <div className="flex items-center justify-center gap-4">
          {/* Mute */}
          <button
            onClick={toggleMute}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
              isMuted ? "bg-white text-black" : "bg-white/20 text-white backdrop-blur-md"
            }`}
          >
            {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
          </button>

          {/* Speaker toggle */}
          <button
            onClick={toggleSpeaker}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
              isSpeaker ? "bg-white/20 text-white backdrop-blur-md" : "bg-white text-black"
            }`}
          >
            {isSpeaker ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
          </button>

          {/* Camera toggle */}
          <button
            onClick={toggleCamera}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
              isCameraOff || !isVideoEnabled ? "bg-white/20 text-white backdrop-blur-md" : "bg-white text-black"
            }`}
          >
            {isCameraOff || !isVideoEnabled ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
          </button>

          {/* Flip camera (front/rear) — only when video is active */}
          {isVideoEnabled && !isCameraOff && (
            <button
              onClick={flipCamera}
              className="w-14 h-14 rounded-full bg-white/20 text-white backdrop-blur-md flex items-center justify-center transition-all active:scale-90"
            >
              <SwitchCamera className="w-6 h-6" />
            </button>
          )}

          {/* End call */}
          <button
            onClick={endCall}
            className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center shadow-lg shadow-red-500/30 hover:bg-red-600 transition-colors active:scale-90"
          >
            <PhoneOff className="w-7 h-7 text-white" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};
